import React from "react";
import { CRISIS, hasHotline } from "../data/crisis";

function telHref(number) {
  return `tel:${String(number).replace(/[^\d+]/g, "")}`;
}

function EmergencyNumbers() {
  const nums = CRISIS.emergencyNumbers;
  return nums.map((n, i) => (
    <React.Fragment key={n}>
      {i > 0 && (i === nums.length - 1 ? " or " : ", ")}
      <a href={telHref(n)} className="crisis-tel">{n}</a>
    </React.Fragment>
  ));
}

function HotlineLink() {
  return (
    <a href={telHref(CRISIS.hotline.number)} className="crisis-tel">
      {CRISIS.hotline.number}
    </a>
  );
}

// " You can also call the <name> at <tel link>." (nothing when blank)
function HotlineSentence() {
  if (!hasHotline()) return null;
  const [before, after = ""] = CRISIS.hotlineSentence.replace("{name}", CRISIS.hotline.name).split("{number}");
  return (
    <>
      {before}
      <HotlineLink />
      {after}
    </>
  );
}

// Renders a CRISIS template string, swapping {numbers} and {hotline} for
// tel: links.
function Template({ text }) {
  const [before, rest = ""] = text.replace("{region}", CRISIS.region).split("{numbers}");
  const [middle, after = ""] = rest.split("{hotline}");
  return (
    <>
      {before}
      <EmergencyNumbers />
      {middle}
      {rest.includes("{hotline}") ? <HotlineSentence /> : null}
      {after}
    </>
  );
}

/**
 * Crisis / emergency notice.
 *
 * variant:
 *  - "compact" (default): one line, for the site footer.
 *  - "form": short note under forms that are not monitored for emergencies.
 *  - "card": highlighted "Crisis Support" card (Resources page).
 */
export default function CrisisNotice({ variant = "compact", className = "" }) {
  if (variant === "card") {
    const headingId = "crisis-support-heading";
    return (
      <section
        className={`card crisis-card ${className}`.trim()}
        aria-labelledby={headingId}
      >
        <h2 id={headingId}>Crisis Support</h2>
        <p>
          Healing Hues is not an emergency service and messages are not monitored
          around the clock. If you or someone else is in immediate danger or at risk
          of harm:
        </p>
        <ul className="crisis-list">
          <li>
            Call <strong><EmergencyNumbers /></strong> (emergency services in {CRISIS.region})
          </li>
          {hasHotline() && (
            <li>
              {CRISIS.hotline.name || "Crisis hotline"}:{" "}
              <strong>
                <HotlineLink />
              </strong>
              {CRISIS.hotline.hours ? ` (${CRISIS.hotline.hours})` : ""}
            </li>
          )}
          <li>Go to the nearest hospital emergency department.</li>
        </ul>
      </section>
    );
  }

  if (variant === "form") {
    return (
      <p className={`crisis-note ${className}`.trim()} role="note">
        <Template text={CRISIS.formNote} />
      </p>
    );
  }

  return (
    <p className={`crisis-compact ${className}`.trim()} role="note">
      <Template text={CRISIS.notice} />
    </p>
  );
}
